import { apiRequest } from "./api.client";

export type KycDocumentType =
  | "aadhar"
  | "pan"
  | "medical_registration"
  | "degree"
  | "drug_license"
  // Clinic institutional docs (clinic_documents) reviewed in the same queue.
  | "clinical_establishment_reg"
  | "abdm_registration"
  | "owner_aadhaar"
  | "owner_pan"
  | "medical_director_nmc"
  // Onboarding business docs (clinic_compliance_documents).
  | "incorporation"
  | "gst"
  | "clinic_registration"
  | "msme"
  | "other_regulatory";

export type KycStatus = "submitted" | "verified" | "rejected";

/** Whether a queue row is a per-doctor KYC doc, clinic institutional doc, or onboarding business doc. */
export type KycSubjectKind = "doctor" | "clinic" | "onboarding";

export interface KycQueueItem {
  kyc_doc_id: string;              // clinic rows reuse clinic_doc_id here
  doctor_id: string | null;        // null for clinic institutional docs
  doctor_name: string;             // "—" for clinic docs
  doc_type: KycDocumentType;       // backend field name
  status: KycStatus;
  submitted_at: string;            // backend field name
  clinic_id: string;
  clinic_name: string | null;
  view_url: string | null;         // presigned URL returned inline (TTL 300s)
  subject_kind?: KycSubjectKind;   // absent (legacy) → treat as "doctor"
  clinic_doc_id?: string;          // present only for clinic rows
}

export interface KycReviewDetail extends KycQueueItem {
  rejection_reason: string | null;
  verified_at: string | null;
  verified_by: string | null;
  other_docs: { doc_type: KycDocumentType; status: KycStatus }[];
}

export const DOCUMENT_TYPE_LABELS: Record<KycDocumentType, string> = {
  aadhar: "Aadhaar Card",
  pan: "PAN Card",
  medical_registration: "Medical Registration",
  degree: "Degree Certificate",
  drug_license: "Drug License",
  clinical_establishment_reg: "Clinical Establishment Certificate",
  abdm_registration: "ABDM Registration",
  owner_aadhaar: "Director / Owner Aadhaar",
  owner_pan: "Director / Owner PAN",
  medical_director_nmc: "Medical Director's NMC Registration",
  incorporation: "Company Incorporation Certificate",
  gst: "GST Certificate",
  clinic_registration: "Clinic Registration Certificate",
  msme: "MSME / Startup India Certificate",
  other_regulatory: "Other Regulatory Document",
};

/** Review-status filter accepted by the queue endpoint. */
export type KycQueueStatusFilter = "submitted" | "verified" | "rejected" | "all";

export const PlatformKycAPI = {
  /**
   * List KYC docs for the platform queue. Defaults to pending ("submitted") to
   * preserve the pending-only callers (e.g. the Onboarded Clinics KYC roll-up);
   * pass "all" to include verified + rejected too.
   */
  listQueue(status: KycQueueStatusFilter = "submitted"): Promise<KycQueueItem[]> {
    return apiRequest<KycQueueItem[]>(`/platform/kyc/pending?status=${encodeURIComponent(status)}`);
  },

  // verify and reject take doctor_id + kyc_doc_id in the path (no clinic_id needed)
  verify(doctorId: string, kycDocId: string): Promise<void> {
    return apiRequest<void>(
      `/platform/kyc/${doctorId}/${kycDocId}/verify`,
      { method: "POST" }
    );
  },

  reject(doctorId: string, kycDocId: string, rejectionReason: string): Promise<void> {
    return apiRequest<void>(
      `/platform/kyc/${doctorId}/${kycDocId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ rejection_reason: rejectionReason }),
      }
    );
  },

  // Clinic institutional docs are keyed by clinic_id + clinic_doc_id.
  verifyClinic(clinicId: string, clinicDocId: string): Promise<void> {
    return apiRequest<void>(
      `/platform/kyc/clinic/${clinicId}/${clinicDocId}/verify`,
      { method: "POST" }
    );
  },

  rejectClinic(clinicId: string, clinicDocId: string, rejectionReason: string): Promise<void> {
    return apiRequest<void>(
      `/platform/kyc/clinic/${clinicId}/${clinicDocId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ rejection_reason: rejectionReason }),
      }
    );
  },

  verifyOnboarding(docId: string): Promise<void> {
    return apiRequest<void>(`/platform/kyc/onboarding/${docId}/verify`, { method: "POST" });
  },

  rejectOnboarding(docId: string, rejectionReason: string): Promise<void> {
    return apiRequest<void>(`/platform/kyc/onboarding/${docId}/reject`, {
      method: "POST",
      body: JSON.stringify({ rejection_reason: rejectionReason }),
    });
  },
};
